import { useState } from "react";
import { Button, Popover } from "flowbite-react";
import { Calendar as CalendarIcon, CaretDown } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { format, subDays, Locale } from "date-fns";
import { enUS, es, de, fr, ja } from "date-fns/locale";
import DateRangePicker from "./index";
import { useIsMobile } from "../../hooks";
import { IDateRangePreset } from "../../interfaces";

// Map of supported locales
const localeMap: { [key: string]: Locale } = {
  en: enUS,
  es: es,
  de: de,
  fr: fr,
  ja: ja,
};

interface DateRangePickerButtonProps {
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
  onPresetChange?: (preset: IDateRangePreset | undefined) => void;
  initialStartDate?: Date;
  initialEndDate?: Date | null;
  initialPreset?: string;
  allowTodaySelection?: boolean;
}

const DateRangePickerButton: React.FC<DateRangePickerButtonProps> = ({
  onDateRangeChange,
  initialStartDate,
  initialEndDate,
  initialPreset,
  onPresetChange,
  allowTodaySelection,
}) => {
  const { i18n } = useTranslation();
  const currentLocale = localeMap[i18n.language] || localeMap.en;
  const today = new Date();
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date>(
    initialStartDate || subDays(today, 29)
  );
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate || today);
  const isMobile = useIsMobile();

  const handleDateRangeChange = (start: Date, end: Date) => {
    setStartDate(start);
    setEndDate(end);
    onDateRangeChange?.(start, end);
    setIsOpen(false);
  };

  const handleTempDateChange = (start: Date, end: Date | null) => {
    setStartDate(start);
    setEndDate(end);
  };

  const formatDateForDisplay = (date: Date) => {
    return format(date, "PP", { locale: currentLocale });
  };

  if (isMobile) {
    return (
      <DateRangePicker
        onDateRangeChange={handleDateRangeChange}
        onTempDateChange={handleTempDateChange}
        initialStartDate={startDate}
        initialEndDate={endDate}
        initialPreset={initialPreset}
        onPresetChange={onPresetChange}
        allowTodaySelection={allowTodaySelection}
      />
    );
  }

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      content={
        <DateRangePicker
          onDateRangeChange={handleDateRangeChange}
          onTempDateChange={handleTempDateChange}
          onClose={() => setIsOpen(false)}
          initialStartDate={startDate}
          initialEndDate={endDate}
          initialPreset={initialPreset}
          onPresetChange={onPresetChange}
          allowTodaySelection={allowTodaySelection}
        />
      }
      theme={{
        content: "z-10 overflow-hidden rounded-lg shadow-lg p-0",
      }}
    >
      <Button
        color="light"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full sm:w-auto min-w-[280px]"
        size={"sm"}
      >
        <div className="flex items-center gap-2">
          <CalendarIcon size={16} />
          <span className="text-xs sm:text-sm whitespace-nowrap">
            {startDate
              ? `${formatDateForDisplay(startDate)}${endDate ? ` - ${formatDateForDisplay(endDate)}` : " - "
              }`
              : "Select date range"}
          </span>
          <CaretDown size={16} />
        </div>
      </Button>
    </Popover>
  );
};

export default DateRangePickerButton;
